import TextField from '@mui/material/TextField'
import MenuItem from '@mui/material/MenuItem'
import FormControlLabel from '@mui/material/FormControlLabel'
import Radio from '@mui/material/Radio'
import Chip from '@mui/material/Chip'
import Popper from '@mui/material/Popper'
import Fade from '@mui/material/Fade'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import { ClickAwayListener } from '@mui/base/ClickAwayListener'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useState, useEffect, useMemo, memo, useCallback } from 'react'
import { useMarkingContext } from '../MarkingContext/MarkingContext'
import '../MarkingMenu/MarkinVariants.css'
import { FontSize } from 'mui-tiptap'
import ChipRating from '../../Setting/Ratings/ChipRating'

const MAX_OPTIONS = 10

const RatingMenu = memo(({ id, rating, studentId, marking, name }) => {
  const { ratings, setCurrentAssessmentArea, onUpdateMarkingStudent } = useMarkingContext()
  const {
    assessmentAreaLevels,
    level,
    elementId,
    subElementId,
    curriculumId,
    assessmentAreaId,
    assessmentAreaRatingType
  } = marking

  const options = [...ratings].filter((r) => r?.type === assessmentAreaRatingType)

  const [selectedOption, setSelectedOptions] = useState(rating)
  const [anchorEl, setAnchorEl] = useState(null)
  const [open, setOpen] = useState(false)
  const [placement, setPlacement] = useState()
  const [search, setSearch] = useState('')
  const [filteredOptions, setFilteredOptions] = useState(options ?? [])

  const handleClick = (newPlacement) => (event) => {
    event.preventDefault()
    setAnchorEl(event?.currentTarget)
    setOpen(true)
    setPlacement(`${newPlacement}-end`)
    setCurrentAssessmentArea(assessmentAreaId)
  }

  const handleClose = (evt) => {
    evt?.stopPropagation()
    setOpen(false)
    setSelectedOptions(rating)
  }

  const handleMenuItemClick = (event, value) => {
    event.stopPropagation()
    const ratingValue = options.find((option) => option?._id === value)
    onUpdateMarkingStudent(
      { studentId, elementId, subElementId, level, rating: ratingValue, assessmentAreaId },
      'rating',
      { ratings, assessmentAreaRatingType }
    )
    setOpen(false)
  }

  const handleSearch = useCallback(
    (value) => {
      setSearch(value)
      if (value.trim() !== '') {
        const filtered = options?.filter((option) => {
          return option?.name?.toLowerCase().includes(value.toLowerCase())
        })
        setFilteredOptions(filtered)
      } else {
        setFilteredOptions(options)
      }
    },
    [options]
  )

  const getAnswerLabel = useMemo(() => {
    const find = options.find((option) => rating === option?._id)

    return {
      value: rating,
      label: find?.name || rating,
      name: find?.name,
      scaleAE: find?.scaleAE,
      primaryColor: find?.primaryColor,
      secondaryColor: find?.secondaryColor,
      isValid: !!find
    }
  }, [options, rating])

  const getInputType = useMemo(() => {
    return filteredOptions.map((item) => (
      <MenuItem
        className='rating-menu__content__item'
        key={item?.value}
        onClick={(evt) => handleMenuItemClick(evt, item?._id)}
      >
        <FormControlLabel
          control={
            <span style={{ display: 'flex', alignItems: 'center' }}>
              <Radio checked={selectedOption === item?._id} style={{ display: 'none' }} />
              {selectedOption === item?._id && (
                <span style={{ color: 'var(--primary)', marginRight: '8px' }}>
                  <FontAwesomeIcon icon='check' />
                </span>
              )}
            </span>
          }
          label={item?.name}
        />
      </MenuItem>
    ))
  }, [filteredOptions, selectedOption])

  useEffect(() => {
    setSelectedOptions(rating)
  }, [rating])

  useEffect(() => {
    if (!open) {
      setFilteredOptions(options)
      setSearch('')
      setCurrentAssessmentArea(null)
    }
  }, [open])

  if (!assessmentAreaRatingType) {
    return null
  }
  if (assessmentAreaRatingType === 'age-expected-scale') {
    return marking?.rating ? (
      <ChipRating
        name={getAnswerLabel?.name}
        scaleAE={getAnswerLabel?.scaleAE}
        primaryColor={getAnswerLabel?.primaryColor}
        secondaryColor={getAnswerLabel?.secondaryColor}
      />
    ) : (
      <></>
    )
  }
  return (
    <ClickAwayListener onClickAway={handleClose}>
      <div id={id} aria-describedby={id} onClick={handleClick('bottom')} style={{ position: 'relative' }}>
        <Chip
          style={{ width: 150 }}
          className={[
            'clickeable text-clickeable cursor-pointer my-1',
            rating ? '' : 'bg-fourth-orange',
            open ? ' selected-chip ' : '',
            getAnswerLabel.isValid ? '' : ' bg-destructive-foreground text-destructive '
          ].join(' ')}
          label={getAnswerLabel.label}
        />

        <Box sx={{ position: 'relative' }}>
          {open && (
            <Popper open={open} anchorEl={anchorEl} placement={placement} transition className='rating-menu'>
              {({ TransitionProps }) => (
                <Fade {...TransitionProps} timeout={100}>
                  <Paper>
                    {options?.length > MAX_OPTIONS && (
                      <Box className='options__search-bar'>
                        <TextField
                          id={`short-ratings-${studentId}-${assessmentAreaId}`}
                          autoFocus
                          name='search'
                          value={search ?? ''}
                          placeholder='Search'
                          onChange={(evt) => handleSearch(evt.target.value)}
                          inputProps={{
                            maxLength: 25,
                            spellCheck: false
                          }}
                          style={{ mr: 1, width: '100%', height: '40px' }}
                          className='custom-text-field'
                          variant='outlined'
                        />
                      </Box>
                    )}
                    <div className='rating-menu__content'>{getInputType}</div>
                  </Paper>
                </Fade>
              )}
            </Popper>
          )}
        </Box>
      </div>
    </ClickAwayListener>
  )
})

RatingMenu.displayName = 'RatingMenu'
export default RatingMenu
